import { useTheme } from "@emotion/react";
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { tokens } from "../../theme";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  Chip,
  IconButton,
  TextField,
  Tooltip,
  Typography,
  Snackbar,
  Alert,
} from "@mui/material";
import PatientService from "../../services/doctorServices/patientService";
import Headeer from "../../components/Headeer";
import {
  DataGrid,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarExport,
} from "@mui/x-data-grid";
import FormService from "../../services/doctorServices/FormService";
import AddToNewFilePatient from "../../components/AddToNewFilePatient";
import ConfirmationDialog from "../../components/ConfirmationDialog";
import DeleteButton from "../../components/DeleteButton";
import { translateBackendError } from "../../utils/errorTranslator";
import WarningSnackbar from "../../components/SnackbarComponent";
import MailIcon from "@mui/icons-material/Mail";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import BarChartIcon from "@mui/icons-material/BarChart";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import VisibilityIcon from "@mui/icons-material/Visibility";

export default function PatientProfile() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation(['patients', 'common']);
  const { id } = useParams();
  const patientService = new PatientService();
  const formService = new FormService();
  const [patientInfo, setPatientInfo] = useState([]);
  const [answeredForms, setAnsweredForms] = useState([]);
  const navigate = useNavigate();
  const [filesPaginationModel, setFilesPaginationModel] = useState({
    page: 0,
    pageSize: 5,
  });
  const [invitationsPaginationModel, setInvitationsPaginationModel] = useState({
    page: 0,
    pageSize: 5,
  });
  const [answeredFormsPaginationModel, setAnsweredFormsPaginationModel] = useState({
    page: 0,
    pageSize: 5,
  });
  const [openAddToNewFileDialog, setOpenAddToNewFileDialog] = useState(false);
  const [formInvitations, setFormInvitations] = useState([]);
  
  // Edit mode states
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedPatient, setEditedPatient] = useState({});
  const [openSnackbar, setOpenSnackbar] = useState(false);
  
  // Add to file snackbar states
  const [addFileSnackbar, setAddFileSnackbar] = useState({ open: false, message: "" });
  
  // Remove file confirmation dialog
  const [removeFileDialogOpen, setRemoveFileDialogOpen] = useState(false);
  const [fileIdToRemove, setFileIdToRemove] = useState(null);

  const dataGridCardStyles = {
    background: `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`,
    borderRadius: "16px",
    border: `1px solid ${colors.grey[800]}30`,
    boxShadow: `0 4px 24px rgba(0, 0, 0, 0.1)`,
    overflow: "hidden",
    width: "100%",
  };

  const dataGridWrapperStyles = {
    px: 3,
    pb: 3,
    minHeight: 360,
    '& .MuiDataGrid-root': {
      border: 'none',
      backgroundColor: 'transparent',
    },
    '& .MuiDataGrid-columnHeaders': {
      backgroundColor: `${colors.blueAccent[700]}60`,
      borderBottom: `2px solid ${colors.blueAccent[500]}40`,
      borderRadius: 0,
      fontWeight: 700,
      fontSize: '0.95rem',
    },
    '& .MuiDataGrid-columnHeaderTitle': {
      fontWeight: 700,
    },
    '& .MuiDataGrid-cell': {
      borderBottom: `1px solid ${colors.grey[800]}20`,
      fontSize: '0.9rem',
    },
    '& .MuiDataGrid-row': {
      transition: 'all 0.3s ease',
      '&:hover': {
        backgroundColor: `${colors.blueAccent[800]}20`,
        cursor: 'pointer',
      },
    },
    '& .MuiDataGrid-row:nth-of-type(even)': {
      backgroundColor: `${colors.primary[500]}20`,
    },
    '& .MuiDataGrid-virtualScroller': {
      backgroundColor: 'transparent',
    },
    '& .MuiDataGrid-footerContainer': {
      backgroundColor: `${colors.blueAccent[700]}40`,
      borderTop: `2px solid ${colors.blueAccent[500]}40`,
      borderRadius: 0,
    },
    '& .MuiTablePagination-root': {
      color: colors.grey[100],
    },
    '& .MuiCheckbox-root': {
      color: `${colors.greenAccent[200]} !important`,
    },
    '& .MuiDataGrid-toolbarContainer': {
      padding: 0,
    },
  };

  const CustomToolbar = ({ placeholder }) => (
    <GridToolbarContainer
      sx={{
        p: 2,
        mb: 2,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 2,
        background: `linear-gradient(135deg, ${colors.blueAccent[800]}20 0%, ${colors.primary[500]} 100%)`,
        borderBottom: `2px solid ${colors.blueAccent[500]}30`,
        borderRadius: '12px 12px 0 0',
        flexWrap: 'wrap',
      }}
    >
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <GridToolbarColumnsButton
          sx={{
            color: colors.grey[100],
            backgroundColor: `${colors.blueAccent[700]}40`,
            border: `1px solid ${colors.blueAccent[500]}40`,
            borderRadius: '10px',
            px: 2,
            py: 1,
            fontWeight: 600,
            fontSize: '0.85rem',
            textTransform: 'none',
            '&:hover': {
              backgroundColor: `${colors.blueAccent[600]}60`,
              transform: 'translateY(-2px)',
              boxShadow: `0 4px 12px ${colors.blueAccent[500]}40`,
            },
            transition: 'all 0.3s ease',
          }}
        />
        <GridToolbarDensitySelector
          sx={{
            color: colors.grey[100],
            backgroundColor: `${colors.blueAccent[700]}40`,
            border: `1px solid ${colors.blueAccent[500]}40`,
            borderRadius: '10px',
            px: 2,
            py: 1,
            fontWeight: 600,
            fontSize: '0.85rem',
            textTransform: 'none',
            '&:hover': {
              backgroundColor: `${colors.blueAccent[600]}60`,
              transform: 'translateY(-2px)',
              boxShadow: `0 4px 12px ${colors.blueAccent[500]}40`,
            },
            transition: 'all 0.3s ease',
          }}
        />
        <GridToolbarExport
          sx={{
            color: 'white',
            background: `linear-gradient(135deg, ${colors.greenAccent[600]} 0%, ${colors.greenAccent[700]} 100%)`,
            border: `1px solid ${colors.greenAccent[500]}40`,
            borderRadius: '10px',
            px: 2,
            py: 1,
            fontWeight: 600,
            fontSize: '0.85rem',
            textTransform: 'none',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 4px 12px ${colors.greenAccent[500]}60`,
            },
            transition: 'all 0.3s ease',
          }}
        />
      </Box>
    </GridToolbarContainer>
  );
  // Fetch patient info function (reusable)
  const fetchPatientInfo = useCallback(async () => {
      try {
        const response = await patientService.getPatientInfo(id);
        console.log("Hasta bilgileri başarıyla geldi: ", response);
        setPatientInfo(response);
      return response;
      } catch (err) {
        console.error("Form oluşturulurken hata: ", err);
        if (err.response?.status === 403) {
          throw err;
        }
        const errorResponse = err.response?.data;
        const errorMessage = errorResponse
          ? translateBackendError(errorResponse)
          : t('errorFetchingPatientInfo');
        alert(errorMessage);
      throw err;
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    const fetchAnsweredForms = async () => {
      try {
        const response = await formService.getAllFormAnswers();
        console.log("getAllFormAnswers response:", response);
        const filteredAnsweredForm = response?.filter(
          (form) => form.patient_id === id
        );
        console.log("filteredAnsweredForm:", filteredAnsweredForm);

        // Backend'den gelen point değerini totalScore olarak kullan
        const withScores = filteredAnsweredForm.map((form) => {
          console.log("Form point değeri:", form.point);
          return {
            ...form,
            totalScore: form.point ? Number(form.point).toFixed(1) : "0.0",
          };
        });

        console.log("withScores:", withScores);
        setAnsweredForms(withScores);
      } catch (err) {
        console.error("Formlar getirilirken hata:", err);
        if (err.response?.status === 403) {
          return;
        }
        alert(t('errorFetchingForms'));
      }
    };

    
    fetchPatientInfo();
    fetchAnsweredForms();
  }, [id, fetchPatientInfo]);

  useEffect(() => {
    const fetchFormInvitations = async () => {
      try {
        console.log("🔍 Patient ID:", id);
        
        // Önce basit yöntemle patient'ın invitation'larını al
        const patientInvitations = await formService.getPatientInvitations(id);
        console.log("📧 Direct Patient Invitations:", patientInvitations);
        
        if (patientInvitations && patientInvitations.length > 0) {
          // Form ID'ye göre grupla
          const formGroups = {};
          
          for (const invitation of patientInvitations) {
            const formId = invitation.form_id;
            
            if (!formGroups[formId]) {
              formGroups[formId] = [];
            }
            formGroups[formId].push(invitation);
          }
          
          console.log("📊 Form Groups:", formGroups);
          
          // Her form grubu için form bilgilerini al
          const formsWithInvitations = [];
          
          for (const [formId, invitations] of Object.entries(formGroups)) {
            try {
              console.log("🔍 Getting form info for:", formId);
              const formResponse = await formService.getFormInfo(formId);
              console.log("📊 Form Response:", formResponse);
              
              // Invitation'ları sequenceNumber'a göre sırala
              const sortedInvitations = invitations.sort((a, b) => (a.sequenceNumber || a.count || 1) - (b.sequenceNumber || b.count || 1));
              
              const formWithInvitations = {
                id: formId, // DataGrid için unique id
                formId: formId,
                formName: formResponse?.name || "Unknown Form",
                fileName: formResponse?.File?.name || "Unknown File",
                invitations: sortedInvitations
              };
              
              console.log("✅ Form with invitations:", formWithInvitations);
              formsWithInvitations.push(formWithInvitations);
            } catch (formErr) {
              console.error(`❌ Form ${formId} için veri alınırken hata:`, formErr);
            }
          }
          
          console.log("🎯 Final Forms with Invitations:", formsWithInvitations);
          setFormInvitations(formsWithInvitations);
        } else {
          console.log("⚠️ No invitations found for patient");
          setFormInvitations([]);
        }
      } catch (err) {
        console.error("❌ Form davetleri getirilirken hata:", err);
        setFormInvitations([]);
      }
    };

    fetchFormInvitations();
  }, [id]);

  const handleDeletePatient = async () => {
    try {
      const response = await patientService.deletePatient(id);
      console.log("Patient Deleted: ", response.data);
      navigate("/patients");
    } catch (error) {
      console.error("Hasta Silinirken hata oluştu: ", error);
      if (error.response?.status === 403) {
        // Axios interceptor + PermissionAlert (Snackbar); alert ile çift uyarı olmasın
        return;
      }
      const errorResponse = error.response?.data;
      const errorMessage = errorResponse
        ? translateBackendError(errorResponse)
        : t('errorDeletingPatient');
      alert(errorMessage);
    }
  };
  const columns = useMemo(() => [
    {
      field: "name",
      headerName: t('fileName'),
      cellClassName: "name-column--cell",
      flex: 1,
    },
    {
      field: "createdAt",
      headerName: t('createdDate'),
      cellClassName: "name-column--cell",
      flex: 1,
      valueFormatter: (params) => {
        return params.value?.split("T")[0]; // "2025-05-13T14:34:13.071Z" → "2025-05-13"
      },
    },
    {
      field: "firstExaminationDate",
      headerName: t('treatmentStartDate'),
      cellClassName: "name-column--cell",
      flex: 1,
      valueGetter: (params) => {
        // Backend'den gelen firstExaminationDate değerini al
        return params.row.firstExaminationDate || null;
      },
      valueFormatter: (params) => {
        if (!params.value) return t('notSet');
        // Backend'den gelen tarih string veya Date objesi olabilir
        const date = typeof params.value === 'string' ? params.value : params.value;
        return date?.split("T")[0] || t('notSet'); // "2025-05-13T14:34:13.071Z" → "2025-05-13"
      },
      renderCell: (params) => {
        // params.row'dan direkt al, valueGetter ile gelen değeri kullan
        const date = params.row.firstExaminationDate || params.value;
        if (!date) {
          return (
            <Typography sx={{ color: colors.grey[500], fontStyle: "italic" }}>
              {t('notSet')}
            </Typography>
          );
        }
        const formattedDate = typeof date === 'string' ? date.split("T")[0] : date;
        return (
          <Typography sx={{ color: colors.grey[100] }}>
            {formattedDate}
          </Typography>
        );
      },
    },
    {
      field: "actions",
      headerName: t('actions'),
      flex: 1,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Button
          variant="contained"
          onClick={() => handleRemoveFile(params.row.id)}
          startIcon={<RemoveCircleOutlineIcon />}
          sx={{
            background: `linear-gradient(135deg, ${colors.redAccent[600]} 0%, ${colors.redAccent[700]} 100%)`,
            color: "white",
            borderRadius: 2,
            px: 2.5,
            py: 1,
            fontWeight: 600,
            textTransform: "none",
            fontSize: "0.85rem",
            boxShadow: `0 4px 12px rgba(219, 79, 74, 0.3)`,
            transition: "all 0.3s ease",
            "&:hover": {
              background: `linear-gradient(135deg, ${colors.redAccent[700]} 0%, ${colors.redAccent[800]} 100%)`,
              transform: "translateY(-2px)",
              boxShadow: `0 6px 16px rgba(219, 79, 74, 0.4)`,
            },
            "&:active": {
              transform: "translateY(0)",
            },
          }}
        >
          {t('removeFromFile')}
        </Button>
      ),
    },
  ], [t, colors]);

  const columnsAnsweredForms = useMemo(() => [
    {
      field: "name",
      headerName: t('formName'),
      cellClassName: "name-column--cell",
      flex: 1,
    },
    {
      field: "Form.File.name",
      headerName: t('fileName'),
      flex: 1,
      valueGetter: (params) => params.row.Form?.File?.name || "",
      cellClassName: "name-column--cell",
    },
    {
      field: "totalScore",
      headerName: t('totalScore'),
      flex: 1,
      cellClassName: "name-column--cell",
    },
    {
      field: "type",
      headerName: t('type'),
      cellClassName: "name-column--cell",
      flex: 1,
    },
    {
      field: "createdAt",
      headerName: t('createdDate'),
      cellClassName: "name-column--cell",
      flex: 1,
      valueFormatter: (params) => {
        return params.value?.split("T")[0]; // "2025-05-13T14:34:13.071Z" → "2025-05-13"
      },
    },
    {
      field: "actions",
      headerName: t('actions'),
      flex: 1,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Tooltip title={t('viewDetails')}>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/patients/${id}/form/${params.row.id}`);
            }}
            sx={{
              backgroundColor: `${colors.blueAccent[700]}40`,
              border: `1px solid ${colors.blueAccent[500]}40`,
              "&:hover": {
                backgroundColor: `${colors.blueAccent[600]}60`,
                transform: "scale(1.1)",
              },
              transition: "all 0.3s ease",
            }}
          >
            <VisibilityIcon sx={{ fontSize: 18, color: colors.blueAccent[300] }} />
          </IconButton>
        </Tooltip>
      ),
    },
  ], [t, colors, id, navigate]);

  const columnsFormInvitations = useMemo(() => [
    {
      field: "fileName",
      headerName: t('fileName'),
      flex: 1,
      cellClassName: "name-column--cell",
    },
    {
      field: "formName",
      headerName: t('formName'),
      flex: 1,
      cellClassName: "name-column--cell",
    },
    {
      field: "invitations",
      headerName: t('formInvitationsHeader'),
      flex: 2,
      cellClassName: "name-column--cell",
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const invitations = params.value || [];
        
        if (invitations.length === 0) {
          return (
            <Typography sx={{ color: colors.grey[500], fontStyle: "italic" }}>
              {t('noInvitationsSent')}
            </Typography>
          );
        }

        return (
          <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
            {invitations.map((invitation, index) => {
              // expires_at kontrolü
              const isExpired = invitation.expires_at && new Date(invitation.expires_at) < new Date();
              
              return (
                <Box
                  key={invitation.id || index}
                  sx={{
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {invitation.status === "pending" ? (
                    <MailIcon 
                      sx={{ 
                        fontSize: 32, 
                        color: isExpired ? colors.redAccent[500] : "#FFC107",
                        filter: isExpired 
                          ? "drop-shadow(0 2px 4px rgba(219, 79, 74, 0.4))"
                          : "drop-shadow(0 2px 4px rgba(255, 193, 7, 0.4))"
                      }} 
                    />
                  ) : (
                    <CheckCircleIcon 
                      sx={{ 
                        fontSize: 32, 
                        color: colors.greenAccent[500],
                        filter: "drop-shadow(0 2px 4px rgba(16, 185, 129, 0.4))"
                      }} 
                    />
                  )}
                  <Box
                    sx={{
                      position: "absolute",
                      top: -6,
                      right: -6,
                      minWidth: 24,
                      height: 24,
                      borderRadius: "50%",
                      backgroundColor: invitation.status === "pending" 
                        ? (isExpired ? colors.redAccent[500] : "#FFC107")
                        : colors.greenAccent[500],
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: `3px solid ${colors.primary[600]}`,
                      boxShadow: `0 3px 12px rgba(0, 0, 0, 0.4)`,
                      zIndex: 1,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: "bold",
                        color: "white",
                        fontSize: "0.75rem",
                        lineHeight: 1,
                        textShadow: "0 1px 2px rgba(0, 0, 0, 0.5)",
                      }}
                    >
                      {invitation.sequenceNumber || invitation.count || index + 1}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        );
      },
    },
  ], [t, colors]);

  const handleRemoveFile = (fileId) => {
    setFileIdToRemove(fileId);
    setRemoveFileDialogOpen(true);
  };

  const handleConfirmRemoveFile = async () => {
    if (!fileIdToRemove) return;

    try {
      const currentFiles = patientInfo?.patient?.Files || [];
      const currentFileIds = patientInfo?.patient?.fileIds || [];

      const updatedFiles = currentFiles.filter(
        (file) => file.id !== fileIdToRemove
      );
      const updatedFileIds = currentFileIds.filter(
        (id) => id !== fileIdToRemove
      );

      const updatedPatientData = {
        ...patientInfo.patient,
        Files: updatedFiles,
        fileIds: updatedFileIds,
      };

      await patientService.updatePatient(id, updatedPatientData);

      // Yeni veriyi setState ile direkt yerleştir
      setPatientInfo((prev) => ({
        ...prev,
        patient: updatedPatientData,
      }));

      setRemoveFileDialogOpen(false);
      setFileIdToRemove(null);
      setOpenSnackbar(true);
    } catch (error) {
      console.error("Dosya kaldırılırken hata oluştu:", error);
      if (error.response?.status !== 403) {
        alert(t('errorRemovingFile'));
      }
      setRemoveFileDialogOpen(false);
      setFileIdToRemove(null);
    }
  };

  const handleCancelRemoveFile = () => {
    setRemoveFileDialogOpen(false);
    setFileIdToRemove(null);
  };

  const handleAddToNewFile = () => {
    setOpenAddToNewFileDialog(true);
  };

  const handleAddToNewFileSuccess = async () => {
    try {
      // Refresh patient info to get updated files
      await fetchPatientInfo();
      // Show success message
      setAddFileSnackbar({
        open: true,
        message: t('patientSuccessfullyAddedToFiles')
      });
    } catch (error) {
      console.error("Error refreshing patient info:", error);
      // Still show success message even if refresh fails
      setAddFileSnackbar({
        open: true,
        message: t('patientSuccessfullyAddedToFiles')
      });
    }
  };

  // Edit mode handlers
  const handleEditMode = () => {
    setIsEditMode(true);
    setEditedPatient({
      firstName: patientInfo?.patient?.firstName || "",
      lastName: patientInfo?.patient?.lastName || "",
      email: patientInfo?.patient?.email || "",
      primaryPhone: patientInfo?.patient?.primaryPhone || "",
      secondaryPhone: patientInfo?.patient?.secondaryPhone || "",
      gender: patientInfo?.patient?.gender || "",
      dateOfBirth: patientInfo?.patient?.dateOfBirth ? patientInfo.patient.dateOfBirth.split('T')[0] : "",
    });
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditedPatient({});
  };

  const handleSavePatient = async () => {
    try {
      const response = await patientService.updatePatient(id, editedPatient);
      console.log("Hasta bilgileri güncellendi:", response);
      
      // Güncellenmiş bilgileri state'e set et
      setPatientInfo({
        ...patientInfo,
        patient: {
          ...patientInfo.patient,
          ...editedPatient
        }
      });
      
      setIsEditMode(false);
      setEditedPatient({});
      setOpenSnackbar(true);
    } catch (error) {
      console.error("Hasta güncellenirken hata oluştu:", error);
      if (error.response?.status === 403) {
        return;
      }
      const errorResponse = error.response?.data;
      const errorMessage = errorResponse
        ? translateBackendError(errorResponse)
        : t('errorUpdatingPatient');
      alert(errorMessage);
    }
  };

  const handleInputChange = (field, value) => {
    setEditedPatient({
      ...editedPatient,
      [field]: value
    });
  };

  const patientFiles = patientInfo?.patient?.Files ?? [];
  const invitationRows = formInvitations ?? [];
  const answeredRows = answeredForms ?? [];

  return (
    <Box sx={{ marginLeft: 2, marginRight: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mr: 2 }}>
        <Headeer
          title={t('patientInformation')}
          subtitle={t('displayPatientInformation')}
        />
        <Box sx={{ display: "flex", gap: 2 }}>
          {isEditMode ? (
            <>
              <Button
                sx={{
                  backgroundColor: colors.greenAccent[700],
                  color: colors.grey[100],
                  fontSize: "14px",
                  fontWeight: "bold",
                  padding: "10px 20px",
                  height: "50%",
                }}
                onClick={handleSavePatient}
              >
                {t('saveChanges')}
              </Button>
              <Button
                sx={{
                  backgroundColor: colors.redAccent[700],
                  color: colors.grey[100],
                  fontSize: "14px",
                  fontWeight: "bold",
                  padding: "10px 20px",
                  height: "50%",
                }}
                onClick={handleCancelEdit}
              >
                {t('cancel')}
              </Button>
            </>
          ) : (
            <>
          <Button
            startIcon={<BarChartIcon />}
            sx={{
              background: `linear-gradient(135deg, ${colors.blueAccent[500]} 0%, ${colors.greenAccent[500]} 100%)`,
              color: colors.grey[100],
              fontSize: "14px",
              fontWeight: "bold",
              padding: "10px 24px",
              height: "50%",
              border: `2px solid ${colors.greenAccent[400]}40`,
              boxShadow: `0 4px 16px ${colors.blueAccent[500]}50`,
              "&:hover": {
                background: `linear-gradient(135deg, ${colors.blueAccent[400]} 0%, ${colors.greenAccent[400]} 100%)`,
                transform: "translateY(-2px)",
                boxShadow: `0 6px 24px ${colors.greenAccent[500]}60`,
              },
              transition: "all 0.3s ease",
            }}
            onClick={() => navigate(`/patients/${id}/statistics`)}
          >
            {t('common:viewStatistics')}
          </Button>
          <Button
            sx={{
              backgroundColor: colors.blueAccent[700],
              color: colors.grey[100],
              fontSize: "14px",
              fontWeight: "bold",
              padding: "10px 20px",
              height: "50%",
                }}
                onClick={handleEditMode}
              >
                {t('editPatientInfo')}
              </Button>
          <Button
            sx={{
              backgroundColor: colors.greenAccent[700],
              color: colors.grey[100],
              fontSize: "14px",
              fontWeight: "bold",
              padding: "10px 20px",
              height: "50%",
            }}
            onClick={handleAddToNewFile}
          >
            {t('addToNewFile')}
          </Button>
            </>
          )}
        </Box>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "60%",
            background: `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`,
            borderRadius: 4,
            boxShadow: `0 8px 32px rgba(0, 0, 0, 0.3)`,
            p: 4,
            border: `2px solid ${colors.primary[200]}`,
            borderTop: `4px solid ${colors.greenAccent[400]}`,
            position: "relative",
            overflow: "hidden",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "4px",
              background: `linear-gradient(90deg, ${colors.greenAccent[400]} 0%, ${colors.blueAccent[400]} 100%)`,
            }
          }}
        >
          {/* Age Badge - Sol Üst Köşe */}
          {patientInfo?.patient?.age && (
            <Box
              sx={{
                position: "absolute",
                top: 16,
                left: 16,
                background: `linear-gradient(135deg, ${colors.blueAccent[600]} 0%, ${colors.greenAccent[600]} 100%)`,
                color: colors.grey[100],
                padding: "10px 18px",
                borderRadius: 3,
                boxShadow: `0 4px 16px rgba(0, 0, 0, 0.4)`,
                zIndex: 10,
                display: "flex",
                alignItems: "center",
                gap: 1,
                border: `2px solid ${colors.primary[200]}`,
                backdropFilter: "blur(10px)",
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.9rem",
                  fontWeight: 700,
                  letterSpacing: "0.5px",
                  textShadow: "0 2px 4px rgba(0, 0, 0, 0.3)",
                }}
              >
                🎂 {patientInfo.patient.age}
              </Typography>
            </Box>
          )}

          {/* Avatar with modern styling */}
          <Box
            sx={{
              position: "relative",
              mb: 3,
              width: "140px",
              height: "140px",
              borderRadius: "50%",
              padding: "4px",
              background: `linear-gradient(135deg, ${colors.greenAccent[500]} 0%, ${colors.blueAccent[500]} 100%)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <img
              alt="profile-user"
              width="132px"
              height="132px"
              src={
                patientInfo?.patient?.gender === "Male"
                  ? `../../assets/avatarPhoto.jpg`
                  : `../../../assets/womenAvatarPhoto.png`
              }
              style={{
                cursor: "pointer",
                borderRadius: "50%",
                objectFit: "cover",
                border: `4px solid ${colors.primary[600]}`,
                boxShadow: `0 4px 16px rgba(0, 0, 0, 0.3)`,
                display: "block",
              }}
            />
          </Box>

          {/* Patient Info Grid */}
          <Box 
            sx={{ 
              display: "grid", 
              gridTemplateColumns: "1fr 1fr", 
              gap: 3,
              width: "100%",
              maxWidth: "800px"
            }}
          >
            {/* Name */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography 
                sx={{ 
                  color: colors.grey[300],
                  fontSize: "0.875rem",
                  fontWeight: "600",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px"
                }}
              >
                {t('firstName')}
              </Typography>
              <TextField
                fullWidth
                variant="filled"
                value={isEditMode ? editedPatient.firstName : (patientInfo?.patient?.firstName || "")}
                onChange={(e) => isEditMode && handleInputChange('firstName', e.target.value)}
                InputProps={{
                  readOnly: !isEditMode,
                  sx: {
                    backgroundColor: isEditMode ? colors.blueAccent[900] : colors.primary[600],
                    borderRadius: 2,
                    "&:hover": {
                      backgroundColor: isEditMode ? colors.blueAccent[800] : colors.primary[500],
                    }
                  }
                }}
              />
            </Box>

            {/* Surname */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography 
                sx={{ 
                  color: colors.grey[300],
                  fontSize: "0.875rem",
                  fontWeight: "600",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px"
                }}
              >
                {t('lastName')}
              </Typography>
              <TextField
                fullWidth
                variant="filled"
                value={isEditMode ? editedPatient.lastName : (patientInfo?.patient?.lastName || "")}
                onChange={(e) => isEditMode && handleInputChange('lastName', e.target.value)}
                InputProps={{
                  readOnly: !isEditMode,
                  sx: {
                    backgroundColor: isEditMode ? colors.blueAccent[900] : colors.primary[600],
                    borderRadius: 2,
                    "&:hover": {
                      backgroundColor: isEditMode ? colors.blueAccent[800] : colors.primary[500],
                    }
                  }
                }}
              />
            </Box>

            {/* Email */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography 
                sx={{ 
                  color: colors.grey[300],
                  fontSize: "0.875rem",
                  fontWeight: "600",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px"
                }}
              >
                {t('email')}
              </Typography>
              <TextField
                fullWidth
                variant="filled"
                type="email"
                value={isEditMode ? editedPatient.email : (patientInfo?.patient?.email || "")}
                onChange={(e) => isEditMode && handleInputChange('email', e.target.value)}
                InputProps={{
                  readOnly: !isEditMode,
                  sx: {
                    backgroundColor: isEditMode ? colors.blueAccent[900] : colors.primary[600],
                    borderRadius: 2,
                    "&:hover": {
                      backgroundColor: isEditMode ? colors.blueAccent[800] : colors.primary[500],
                    }
                  }
                }}
              />
            </Box>

            {/* Primary Phone */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography 
                sx={{ 
                  color: colors.grey[300],
                  fontSize: "0.875rem",
                  fontWeight: "600",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px"
                }}
              >
                {t('primaryPhone')}
              </Typography>
              <TextField
                fullWidth
                variant="filled"
                value={isEditMode ? editedPatient.primaryPhone : (patientInfo?.patient?.primaryPhone || "")}
                onChange={(e) => isEditMode && handleInputChange('primaryPhone', e.target.value)}
                InputProps={{
                  readOnly: !isEditMode,
                  sx: {
                    backgroundColor: isEditMode ? colors.blueAccent[900] : colors.primary[600],
                    borderRadius: 2,
                    "&:hover": {
                      backgroundColor: isEditMode ? colors.blueAccent[800] : colors.primary[500],
                    }
                  }
                }}
              />
            </Box>

            {/* Secondary Phone */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography 
                sx={{ 
                  color: colors.grey[300],
                  fontSize: "0.875rem",
                  fontWeight: "600",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px"
                }}
              >
                {t('secondaryPhone')}
              </Typography>
              <TextField
                fullWidth
                variant="filled"
                value={isEditMode ? editedPatient.secondaryPhone : (patientInfo?.patient?.secondaryPhone || "")}
                onChange={(e) => isEditMode && handleInputChange('secondaryPhone', e.target.value)}
                InputProps={{
                  readOnly: !isEditMode,
                  sx: {
                    backgroundColor: isEditMode ? colors.blueAccent[900] : colors.primary[600],
                    borderRadius: 2,
                    "&:hover": {
                      backgroundColor: isEditMode ? colors.blueAccent[800] : colors.primary[500],
                    }
                  }
                }}
              />
            </Box>

            {/* Gender */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography 
                sx={{ 
                  color: colors.grey[300],
                  fontSize: "0.875rem",
                  fontWeight: "600",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px"
                }}
              >
                {t('gender')}
              </Typography>
              <TextField
                fullWidth
                variant="filled"
                value={isEditMode ? editedPatient.gender : (patientInfo?.patient?.gender || "")}
                onChange={(e) => isEditMode && handleInputChange('gender', e.target.value)}
                InputProps={{
                  readOnly: !isEditMode,
                  sx: {
                    backgroundColor: isEditMode ? colors.blueAccent[900] : colors.primary[600],
                    borderRadius: 2,
                    "&:hover": {
                      backgroundColor: isEditMode ? colors.blueAccent[800] : colors.primary[500],
                    }
                  }
                }}
              />
            </Box>

            {/* Date of Birth */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography 
                sx={{ 
                  color: colors.grey[300],
                  fontSize: "0.875rem",
                  fontWeight: "600",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px"
                }}
              >
                {t('dateOfBirth')}
              </Typography>
              <TextField
                fullWidth
                variant="filled"
                type="date"
                value={isEditMode ? editedPatient.dateOfBirth : (patientInfo?.patient?.dateOfBirth ? patientInfo.patient.dateOfBirth.split('T')[0] : "")}
                onChange={(e) => isEditMode && handleInputChange('dateOfBirth', e.target.value)}
                InputProps={{
                  readOnly: !isEditMode,
                  sx: {
                    backgroundColor: isEditMode ? colors.blueAccent[900] : colors.primary[600],
                    borderRadius: 2,
                    "&:hover": {
                      backgroundColor: isEditMode ? colors.blueAccent[800] : colors.primary[500],
                    }
                  }
                }}
              />
            </Box>

            {/* BMI */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography 
                sx={{ 
                  color: colors.grey[300],
                  fontSize: "0.875rem",
                  fontWeight: "600",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px"
                }}
              >
                {t('bmi')}
              </Typography>
              <TextField
                fullWidth
                variant="filled"
                value={patientInfo?.patient?.bmi ? `${patientInfo.patient.bmi}` : t('notAvailable')}
                InputProps={{
                  readOnly: true,
                  sx: {
                    backgroundColor: colors.primary[600],
                    borderRadius: 2,
                    "&:hover": {
                      backgroundColor: colors.primary[500],
                    }
                  }
                }}
              />
            </Box>

            {/* Doctor */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, gridColumn: "span 2" }}>
              <Typography 
                sx={{ 
                  color: colors.grey[300],
                  fontSize: "0.875rem",
                  fontWeight: "600",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px"
                }}
              >
                {t('doctor')}
              </Typography>
              <TextField
                fullWidth
                variant="filled"
                value={
                  patientInfo?.patient?.Doctor?.name +
                    " " +
                    patientInfo?.patient?.Doctor?.surname || ""
                }
                InputProps={{
                  readOnly: true,
                  sx: {
                    backgroundColor: colors.primary[600],
                    borderRadius: 2,
                    "&:hover": {
                      backgroundColor: colors.primary[500],
                    }
                  }
                }}
              />
            </Box>
          </Box>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 5, mt: 6, width: "100%" }}>
          <Card sx={{ ...dataGridCardStyles, width: "95%" }}>
        <Box
          sx={{
            display: "flex",
                justifyContent: "space-between",
            alignItems: "center",
                px: 4,
                py: 3,
                borderBottom: `1px solid ${colors.grey[800]}30`,
          }}
        >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 3,
                    background: `linear-gradient(135deg, ${colors.blueAccent[500]} 0%, ${colors.greenAccent[500]} 100%)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
                    boxShadow: `0 8px 20px ${colors.primary[900]}40`,
                    color: colors.grey[100],
                  }}
                >
                  <FolderOpenIcon />
                </Box>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: colors.grey[100], letterSpacing: 0.3 }}>
                    {t('filesAssociatedWithPatient')}
                  </Typography>
                  <Typography variant="body2" sx={{ color: colors.grey[300], mt: 0.5, opacity: 0.85 }}>
                    {t('managePatientFilesDescription')}
                  </Typography>
                </Box>
              </Box>
              <Chip
                label={`${patientFiles.length} ${patientFiles.length === 1 ? t('file') : t('files')}`}
                sx={{
                  backgroundColor: `${colors.blueAccent[700]}40`,
                  color: colors.blueAccent[100],
                  fontWeight: 600,
                  borderRadius: 2,
                  px: 1.5,
                }}
              />
            </Box>
            <Box sx={dataGridWrapperStyles}>
            <DataGrid
                rows={patientFiles}
              columns={columns}
              autoHeight
              pagination
              paginationModel={filesPaginationModel}
                onPaginationModelChange={setFilesPaginationModel}
                pageSizeOptions={[5, 10, 25, 50]}
                disableRowSelectionOnClick
                slots={{ toolbar: CustomToolbar }}
                slotProps={{ toolbar: { placeholder: t('searchFiles') } }}
            />
          </Box>
          </Card>

          <Card sx={{ ...dataGridCardStyles, width: "95%" }}>
        <Box
          sx={{
            display: "flex",
                justifyContent: "space-between",
            alignItems: "center",
                px: 4,
                py: 3,
                borderBottom: `1px solid ${colors.grey[800]}30`,
          }}
        >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 3,
                    background: `linear-gradient(135deg, ${colors.greenAccent[500]} 0%, ${colors.blueAccent[500]} 100%)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
                    boxShadow: `0 8px 20px ${colors.primary[900]}40`,
                    color: colors.grey[100],
                  }}
                >
                  <EmailOutlinedIcon />
                </Box>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: colors.grey[100], letterSpacing: 0.3 }}>
                    {t('submittedFormsStatus')}
                  </Typography>
                  <Typography variant="body2" sx={{ color: colors.grey[300], mt: 0.5, opacity: 0.85 }}>
                    {t('trackInvitationHistory')}
                  </Typography>
                </Box>
              </Box>
              <Chip
                label={`${invitationRows.length} ${invitationRows.length === 1 ? t('record') : t('records')}`}
                sx={{
                  backgroundColor: `${colors.blueAccent[700]}40`,
                  color: colors.blueAccent[100],
                  fontWeight: 600,
                  borderRadius: 2,
                  px: 1.5,
            }}
              />
            </Box>
            <Box sx={dataGridWrapperStyles}>
            <DataGrid
                rows={invitationRows}
              columns={columnsFormInvitations}
              autoHeight
              pagination
              paginationModel={invitationsPaginationModel}
              onPaginationModelChange={setInvitationsPaginationModel}
                pageSizeOptions={[5, 10, 25, 50]}
                disableRowSelectionOnClick
                slots={{ toolbar: CustomToolbar }}
                slotProps={{ toolbar: { placeholder: t('searchInvitations') } }}
              getRowId={(row) => row.id}
            />
          </Box>
          </Card>

          <Card sx={{ ...dataGridCardStyles, width: "95%" }}>
        <Box
          sx={{
            display: "flex",
                justifyContent: "space-between",
            alignItems: "center",
                px: 4,
                py: 3,
                borderBottom: `1px solid ${colors.grey[800]}30`,
          }}
        >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 3,
                    background: `linear-gradient(135deg, ${colors.blueAccent[500]} 0%, ${colors.greenAccent[600]} 100%)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
                    boxShadow: `0 8px 20px ${colors.primary[900]}40`,
                    color: colors.grey[100],
                  }}
                >
                  <AssignmentTurnedInIcon />
                </Box>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: colors.grey[100], letterSpacing: 0.3 }}>
                    {t('filledFormsForPatient')}
                  </Typography>
                  <Typography variant="body2" sx={{ color: colors.grey[300], mt: 0.5, opacity: 0.85 }}>
                    {t('reviewCompletedForms')}
                  </Typography>
                </Box>
              </Box>
              <Chip
                label={`${answeredRows.length} ${answeredRows.length === 1 ? t('form') : t('forms')}`}
                sx={{
                  backgroundColor: `${colors.blueAccent[700]}40`,
                  color: colors.blueAccent[100],
                  fontWeight: 600,
                  borderRadius: 2,
                  px: 1.5,
                }}
              />
            </Box>
            <Box sx={dataGridWrapperStyles}>
            <DataGrid
                rows={answeredRows}
              columns={columnsAnsweredForms}
              autoHeight
              pagination
              paginationModel={answeredFormsPaginationModel}
                onPaginationModelChange={setAnsweredFormsPaginationModel}
                pageSizeOptions={[5, 10, 25, 50]}
                disableRowSelectionOnClick
                slots={{ toolbar: CustomToolbar }}
                slotProps={{ toolbar: { placeholder: t('searchFilledForms') } }}
            />
          </Box>
          </Card>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "flex-end",
            width: "95%",
            m: 2,
            mt: 5,
          }}
        >
          <DeleteButton
            label={t('deletePatient')}
            onConfirm={handleDeletePatient}
            confirmationTitle={t('deletePatient')}
            confirmationDescription={t('deletePatientConfirmation')}
            variant="medium"
          />
        </Box>

        {openAddToNewFileDialog && (
          <AddToNewFilePatient
            open={openAddToNewFileDialog}
            onClose={() => setOpenAddToNewFileDialog(false)}
            patientId={patientInfo?.patient?.id}
            onApplyFiles={handleAddToNewFileSuccess}
          />
        )}

        {/* Remove File Confirmation Dialog */}
        <ConfirmationDialog
          open={removeFileDialogOpen}
          onClose={handleCancelRemoveFile}
          onConfirm={handleConfirmRemoveFile}
          title={t('removePatientFromFile')}
          description={
            fileIdToRemove
              ? t('removePatientFromFileConfirmation', { fileName: patientInfo?.patient?.Files?.find((f) => f.id === fileIdToRemove)?.name || "this file" })
              : t('removePatientFromFileConfirmationGeneric')
          }
        />

        {/* Success Snackbar */}
        <Snackbar
          open={openSnackbar}
          autoHideDuration={4000}
          onClose={() => setOpenSnackbar(false)}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
        >
          <Alert
            onClose={() => setOpenSnackbar(false)}
            severity="success"
            variant="filled"
            sx={{
              width: "100%",
              borderRadius: "12px",
              backgroundColor: colors.greenAccent[500],
              boxShadow: `0 4px 16px rgba(76, 206, 172, 0.4)`,
              "& .MuiAlert-icon": {
                fontSize: "28px",
              },
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: "bold", color: "white" }}>
              {t('operationCompletedSuccessfully')}
            </Typography>
            <Typography variant="body2" sx={{ color: "white", mt: 0.5 }}>
              {t('changesHaveBeenSaved')}
            </Typography>
          </Alert>
        </Snackbar>

        {/* Add to File Success Snackbar */}
        <WarningSnackbar
          open={addFileSnackbar.open}
          autoHideDuration={4000}
          onClose={() => setAddFileSnackbar({ open: false, message: "" })}
          message={addFileSnackbar.message}
          severity="success"
        />
      </Box>
    </Box>
  );
}
